import React, { useState, useEffect } from 'react';
import styles from './RtfViewer.module.css';

const RtfViewer = ({ rtfContent }) => {
  const [content, setContent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!rtfContent) {
      setLoading(false);
      setError('No URL provided');
      return;
    }

    const fetchAndParseRtf = async () => {
      try {
        setLoading(true);
        setError(null);
        
        const response = await fetch(rtfContent);
        if (!response.ok) {
          throw new Error(`Failed to fetch: ${response.statusText}`);
        }
        
        const text = await response.text();
        const html = rtfToHtml(text);
        setContent(html);
      } catch (err) {
        setError(err.message);
        setContent(null);
      } finally {
        setLoading(false);
      }
    };

    fetchAndParseRtf();
  }, [rtfContent]);

  const rtfToHtml = (rtf) => {
    let text = rtf;

    // initial cleanup (remove long hex/binary sequences, URLs, big tables, etc.)
    text = text.replace(/\*[0-9a-f]{100,}/gi, '');
    text = text.replace(/[0-9a-f]{500,}/gi, '');
    text = text.replace(/https?:\/\/[^\s\}]*/gi, '');
    text = text.replace(/http:\/\/[^\s\}]*/gi, '');
    text = text.replace(/\{\\fonttbl[\s\S]*?\}\}/g, '');
    text = text.replace(/\{\\colortbl[^}]*\}/g, '');
    text = text.replace(/\{\\stylesheet[\s\S]*?\}\}/g, '');
    text = text.replace(/\{\\info[\s\S]*?\}\}/g, '');
    text = text.replace(/\{\\[*]\\[a-z]+generator[\s\S]*?\}/gi, '');
    text = text.replace(/\{\\[*]\\listtable[\s\S]*?\}\}/gi, '');
    text = text.replace(/\{\\[*]\\listoverridetable[\s\S]*?\}\}/gi, '');
    text = text.replace(/\{\\[*]\\rsidtbl[\s\S]*?\}\}/gi, '');
    text = text.replace(/\{\\[*]\\datastore[\s\S]*?\}\}/gi, '');
    text = text.replace(/\{\\[*]\\themedata[\s\S]*?\}\}/gi, '');
    text = text.replace(/\{\\[*]\\colorschememapping[\s\S]*?\}\}/gi, '');
    text = text.replace(/\{\\[*]\\latentstyles[\s\S]*?\}\}/gi, '');
    // remove obvious leading numeric/metadata tokens
    text = text.replace(/^[\d\*\(\)\.\s\-\\:;,_]{1,200}/g, '');

    let html = '';
    let i = 0;
    let bold = false;
    let italic = false;
    let underline = false;
    const stack = [];
    let skipGroup = false;
    
    while (i < text.length) {
      const char = text[i];

      if (char === '{') {
        stack.push({ bold, italic, underline, skipGroup });
        i++;
      } else if (char === '}') {
        const state = stack.pop();
        if (state) {
          if (bold && !state.bold) html += '</strong>';
          if (italic && !state.italic) html += '</em>';
          if (underline && !state.underline) html += '</u>';
          bold = state.bold;
          italic = state.italic;
          underline = state.underline;
          skipGroup = state.skipGroup;
        }
        i++;
      } else if (char === '\\') {
        i++;
        let cmd = '';

        while (i < text.length && /[a-z*]/i.test(text[i])) {
          cmd += text[i];
          i++;
        }

        let param = '';
        if (i < text.length && text[i] === '-') param += text[i++];
        while (i < text.length && /[0-9]/.test(text[i])) param += text[i++];
        if (i < text.length && text[i] === ' ') i++;

        if (skipGroup) continue;

        if (cmd === 'par' || cmd === 'line') html += '<br>';
        else if (cmd === 'tab') html += '&nbsp;&nbsp;&nbsp;&nbsp;';
        else if (cmd === 'b') {
          if (param === '0') { if (bold) html += '</strong>'; bold = false; }
          else { if (!bold) html += '<strong>'; bold = true; }
        }
        else if (cmd === 'i') {
          if (param === '0') { if (italic) html += '</em>'; italic = false; }
          else { if (!italic) html += '<em>'; italic = true; }
        }
        else if (cmd === 'ul' || cmd === 'ulw') {
          if (!underline) html += '<u>';
          underline = true;
        }
        else if (cmd === 'ulnone' || cmd === 'ul0') {
          if (underline) html += '</u>';
          underline = false;
        }
        else if (cmd === "'") {
          const hex = text.substr(i, 2);
          i += 2;
          const code = parseInt(hex, 16);
          if (code >= 32 && code < 127) html += String.fromCharCode(code);
        }

      } else {
        if (!skipGroup && char.charCodeAt(0) >= 32) html += char;
        i++;
      }
    }

    if (underline) html += '</u>';
    if (italic) html += '</em>';
    if (bold) html += '</strong>';

    // normalize whitespace and reduce repeated breaks
    html = html.replace(/\s+/g, ' ');
    html = html.replace(/(<br>\s*){3,}/g, '<br><br>');
    html = html.trim();

    // NEW: remove any non-letter/punct sequence at the start (this strips junk like "2450...)()()()()()")
    // but keep HTML tags if content starts with them
    html = html.replace(/^[^A-Za-z<]+/, '');

    // final trim
    html = html.trim();

    return html;
  };

  if (loading) {
    return (
      <div className={styles.container}>
        <div className={styles.loading}>Loading RTF file...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.container}>
        <div className={styles.errorBox}>
          <div>
            <div className={styles.errorTitle}>Error loading RTF file</div>
            <div>{error}</div>
          </div>
        </div>
      </div>
    );
  }

  if (!content) {
    return <div className={styles.container}>No content to display</div>;
  }

  return (
    <div className={styles.container}>
      <div
        className={styles.contentArea}
        dangerouslySetInnerHTML={{ __html: content }}
      />
    </div>
  );
};

export default RtfViewer;
